import moment, { Moment } from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './styles.less';
import ActionIcon from '../_common/ActionIcon';
import { Group } from '../_common';
import { Col, DatePicker, Row, Segmented, Tag } from 'antd';
import { ArrowLeft, Calendar, ChevronLeft, ChevronRight } from 'react-feather';
import AgendaCard, { AgendaData } from './AgendaCard';
import { useHorizontalEdgeScroll, useWindowVerticalEdgeScroll } from './hook';

enum AgendaType {
  Day = 'day',
  Week = 'week',
  Month = 'month',
}

interface DataSource extends AgendaData {
  onClick?: VoidFunction;
}

interface Props {
  dataSource: DataSource[];
  onDragEnd?(newItem: AgendaData, prevItem: AgendaData): void;
  enableDraggable?: boolean;
  date: Moment;
  onChangeDate?(date: Moment): void;
}

export default function Agenda({
  dataSource,
  onDragEnd,
  enableDraggable = false,
  date,
  onChangeDate,
}: Props) {
  const [type, setType] = useState(AgendaType.Week);
  const [colsCount, setColsCount] = useState<Moment[]>([]);

  const [data, setData] = useState<DataSource[]>(dataSource);

  const isCurrentDayInCol = useMemo(
    () => colsCount.some((day) => day.dayOfYear() === moment().dayOfYear()),
    [colsCount],
  );

  useEffect(() => {
    setData(dataSource);
  }, [dataSource]);

  useEffect(() => {
    handler(type, date);
  }, [type, date]);

  const handleColsCount = (count: number, currentDate: Moment) => {
    const days = [];

    for (let i = 0; i < count; i++) {
      days.push(moment(currentDate).add(i, 'days'));
    }

    setColsCount(days);
  };

  const handler = (value: AgendaType, currentDate: Moment) => {
    setType(value);

    switch (value) {
      case AgendaType.Day:
        handleColsCount(1, moment(currentDate).startOf('day'));
        break;
      case AgendaType.Week:
        handleColsCount(7, moment(currentDate).startOf('week'));
        break;
      case AgendaType.Month:
        handleColsCount(
          moment(currentDate).daysInMonth(),
          moment(currentDate).startOf('month'),
        );
        break;
    }
  };

  const goTodoay = () => {
    const element = document.querySelector(
      `[data-date="${moment().format('YYYY-MM-DD')}"]`,
    );

    element?.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: `nearest`,
    });
  };

  const gotToFirstDayInMonth = (date: Moment) => {
    const element = document.querySelector(
      `[data-date="${date.startOf('month').format('YYYY-MM-DD')}"]`,
    );

    element?.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: `nearest`,
    });
  };

  const onPrev = useCallback(() => {
    const newDate = moment(date).clone().subtract(1, type);

    onChangeDate?.(newDate);

    if (type === AgendaType.Month) {
      setTimeout(() => gotToFirstDayInMonth(newDate), 1000);
    }
  }, [type, date]);

  const onNext = useCallback(() => {
    const newDate = moment(date).clone().add(1, type);

    onChangeDate?.(newDate);

    if (type === AgendaType.Month) {
      setTimeout(() => gotToFirstDayInMonth(newDate), 150);
    }
  }, [type, date]);

  const inputDateRef = useRef(null);

  function handleDragStart(e: React.DragEvent<HTMLDivElement>, id: string) {
    e.dataTransfer.setData('text/plain', id);
  }

  function handleDragOver(e: React.DragEvent<HTMLTableCellElement>) {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = '#f0f0f0';
  }

  function handleDragLeave(e: React.DragEvent<HTMLTableCellElement>) {
    e.currentTarget.style.backgroundColor = '';
  }

  function handleDrop(
    e: React.DragEvent<HTMLTableCellElement>,
    day: string,
    time: string,
  ) {
    e.preventDefault();
    const id = e.dataTransfer.getData('text/plain');

    setData((prev) =>
      prev.map((item) => (item.id === id ? { ...item, day, time } : item)),
    );

    if (onDragEnd) {
      const prevItem = data.find((item) => item.id === id);

      if (prevItem) {
        onDragEnd({ ...prevItem, day, time }, prevItem);
      }
    }

    e.currentTarget.style.backgroundColor = '';
    e.dataTransfer.clearData();
  }

  const horizontalEdgeScroll = useHorizontalEdgeScroll();

  const windowVerticalEdgeScroll = useWindowVerticalEdgeScroll();

  return (
    <div onDragOver={windowVerticalEdgeScroll.handleDragOver}>
      <div className="agenda-filter" id="agenda-filter">
        <Row gutter={[16, 16]}>
          <Col>
            <Group style={{ height: '100%' }}>
              <ActionIcon
                onClick={() => {
                  onChangeDate?.(moment());
                  if (type === AgendaType.Month) {
                    setTimeout(goTodoay, 150);
                  }
                }}
                style={{ height: '100%' }}
              >
                <Group gap={10} style={{ height: '100%' }}>
                  {isCurrentDayInCol ? (
                    <Calendar size={16} />
                  ) : (
                    <ArrowLeft size={16} />
                  )}
                  <span style={{ fontWeight: 500 }}>Hoje</span>
                </Group>
              </ActionIcon>
            </Group>
          </Col>

          <Col flex={1}>
            <Group gap={10} align="center" style={{ height: '100%' }}>
              <ActionIcon onClick={onPrev}>
                <ChevronLeft size={16} />
              </ActionIcon>

              <DatePicker.MonthPicker
                value={date}
                onChange={(inputValue) => {
                  const newDate = (inputValue || moment()).startOf('month');

                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (inputDateRef.current as any)?.blur();

                  onChangeDate?.(newDate);

                  if (type === AgendaType.Month) {
                    setTimeout(() => gotToFirstDayInMonth(newDate), 150);
                  }
                }}
                format="MMMM - YYYY"
                allowClear={false}
                ref={inputDateRef}
                suffixIcon={null}
                className="agenda-filter-date"
                inputReadOnly
              />

              <ActionIcon onClick={onNext}>
                <ChevronRight size={16} />
              </ActionIcon>
            </Group>
          </Col>

          <Col className="agenda-filter-col-type">
            <Segmented
              className="agenda-filter-type"
              value={type}
              onChange={(value) => {
                onChangeDate?.(moment());
                handler(value as AgendaType, moment());
                if (value === AgendaType.Month) {
                  setTimeout(() => goTodoay(), 150);
                }
              }}
              options={[
                { value: AgendaType.Day, label: 'Dia' },
                { value: AgendaType.Week, label: 'Semana' },
                { value: AgendaType.Month, label: 'Mês' },
              ]}
            />
          </Col>
        </Row>
      </div>

      <div className="agenda-table-scroll" ref={horizontalEdgeScroll.innerRef}>
        <table className="agenda-table">
          <thead>
            <tr>
              <th
                className="agenda-cell-hour"
                style={{
                  width: type === AgendaType.Day ? '1%' : '5%',
                }}
              />

              {colsCount.map((day, i) => {
                const isCurrentDay = day.dayOfYear() === moment().dayOfYear();
                const isEndWeek = day.isSame(moment(day).endOf('week'), `day`);

                const totalItems = data.filter(
                  (item) => item.day === day.format('YYYY-MM-DD'),
                ).length;

                return (
                  <th
                    className="agenda-cell agenda-header-week"
                    data-current={isCurrentDay}
                    data-endweek={isEndWeek && type === AgendaType.Month}
                    data-date={day.format('YYYY-MM-DD')}
                    key={i}
                  >
                    <div className="agenda-header-week-info">
                      <div className="info-day">
                        <div className="info-day-number">
                          {day.format('DD')}
                        </div>
                        <div className="info-day-label">
                          {day.format('dddd')}
                        </div>
                      </div>

                      <Tag style={{ height: `fit-content` }}>{totalItems}</Tag>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {Array.from({ length: 24 }).map((_, h) => {
              const isCurrentHour = isCurrentDayInCol && moment().hours() === h;

              return (
                <tr key={h} className="agenda-row">
                  <td
                    className="agenda-cell agenda-cell-hour"
                    data-currenthour={isCurrentHour}
                  >
                    {`${h}h`}
                  </td>

                  {colsCount.map((day, i) => {
                    const isCurrentDay =
                      day.dayOfYear() === moment().dayOfYear();

                    const isEndWeek = day.isSame(
                      moment(day).endOf('week'),
                      `day`,
                    );

                    const items = data
                      .filter(
                        (item) =>
                          item.day === day.format('YYYY-MM-DD') &&
                          item.time.split(':')[0] ===
                            String(h).padStart(2, '0'),
                      )
                      .sort((a, b) => a.time.localeCompare(b.time));

                    return (
                      <td
                        className="agenda-cell agenda-cell-content"
                        data-current={isCurrentDay}
                        data-endweek={isEndWeek && type === AgendaType.Month}
                        data-currenthour={isCurrentHour}
                        key={i}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) =>
                          handleDrop(
                            e,
                            day.format('YYYY-MM-DD'),
                            `${String(h).padStart(2, '0')}:00`,
                          )
                        }
                      >
                        <div className="agenda-cell-wrap">
                          {items.map(({ onClick, ...item }) => (
                            <div
                              key={item.id}
                              draggable={enableDraggable}
                              className="agenda-card-draggable"
                              onDragStart={(e) => {
                                handleDragStart(e, item.id);
                                windowVerticalEdgeScroll.handleDragStart();
                              }}
                              onDragEnd={() => {
                                windowVerticalEdgeScroll.handleDragEnd();
                              }}
                              onClick={onClick}
                              style={{
                                cursor: enableDraggable ? 'grab' : 'pointer',
                              }}
                            >
                              <AgendaCard item={item} />
                            </div>
                          ))}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
